import { type CSSProperties, type ReactNode } from 'react';
import {
  getBrandColor,
  getBrandFont,
  type BrandColorRole,
  type DraftBrandTokens,
} from '@erp/json-renderer';

/**
 * Frontend-only bridge from brand-kit tokens to the `@erp/ui` design system.
 *
 * `@erp/ui` is themed entirely through CSS custom properties (`--primary`,
 * `--background`, …) defined in `packages/ui/src/styles.css`. To render a
 * customer's portal in their brand, we map their `DraftBrandTokens` color/font
 * roles onto those same variables and scope them to a wrapper `<div>`. Every
 * shadcn primitive underneath (`Button`, `Card`, `Input`, `Badge`, …) then
 * paints in the brand without any per-component theming.
 *
 * This deliberately lives in the frontend and only *consumes* the brand-kit
 * model (`getBrandColor`/`getBrandFont` from `@erp/json-renderer`) — it adds no
 * theming code to the json-renderer package.
 *
 * Graceful fallback: roles that aren't present in the tokens set no variable,
 * so the inherited Gently (`@erp/ui` stone) defaults show through unchanged.
 */

/**
 * Map a brand color role to the `@erp/ui` CSS variables it should drive. A
 * single role often feeds several variables (e.g. `surface` themes both cards
 * and popovers) so the whole component set stays coherent.
 */
const COLOR_ROLE_TO_VARS: Partial<Record<BrandColorRole, string[]>> = {
  primary: ['--primary', '--ring'],
  onPrimary: ['--primary-foreground'],
  secondary: ['--secondary'],
  onSecondary: ['--secondary-foreground'],
  accent: ['--accent'],
  background: ['--background'],
  surface: ['--card', '--popover'],
  textPrimary: ['--foreground', '--card-foreground', '--popover-foreground'],
  textSecondary: ['--muted-foreground', '--accent-foreground'],
  border: ['--border', '--input'],
  danger: ['--destructive'],
};

/**
 * Build the CSS-variable overrides for a set of brand tokens. Returns a plain
 * style object; absent roles are simply omitted (no override → inherits the
 * `@erp/ui` default).
 */
function buildBrandStyle(tokens: DraftBrandTokens): CSSProperties {
  const style: Record<string, string> = {};

  for (const [role, vars] of Object.entries(COLOR_ROLE_TO_VARS)) {
    const value = getBrandColor(tokens, role as BrandColorRole);
    if (!value || !vars) continue;
    for (const cssVar of vars) {
      style[cssVar] = value;
    }
  }

  const bodyFont = getBrandFont(tokens, 'body');
  if (bodyFont) {
    style.fontFamily = bodyFont.family;
  }

  return style as CSSProperties;
}

interface PortalBrandThemeProps {
  tokens: DraftBrandTokens;
  children: ReactNode;
  className?: string;
}

/**
 * Scope a subtree to a brand kit's theme. Sets brand-derived CSS variables on a
 * wrapper and paints the default surface/text colors so the region reads as the
 * brand even outside an explicit `Card`.
 */
export function PortalBrandTheme({
  tokens,
  children,
  className,
}: PortalBrandThemeProps) {
  return (
    <div
      className={className}
      style={{
        ...buildBrandStyle(tokens),
        backgroundColor: 'var(--background)',
        color: 'var(--foreground)',
      }}
    >
      {children}
    </div>
  );
}
